'use client';

// STREAM-F3 — Globaler Theme-Hook für Landing + Funnel + Portal.
//
// Cookie-basiert ('37s_theme'), damit der Theme-Zustand server-/client-shared
// bleibt und Page-Transitions (Landing -> /bewerben -> /a/...) ohne Flicker
// ueberlebt. Der initiale data-theme-Wert wird vom No-FOUC-Script im
// RootLayout aus dem gleichen Cookie gesetzt, bevor React hydratisiert -
// der Hook liest den aktuellen Wert hier nur noch nach Mount aus, damit
// Hydration-Diffs ausbleiben.
//
// Drei-Wert-Modell:
//   - 'light' / 'dark'   explizite User-Wahl
//   - 'system'           folgt prefers-color-scheme (Default-Fallback)
//
// Public API:
//   - theme    Aufgeloester Wert ('light' | 'dark'), wird auf data-theme gesetzt
//   - pref     Roh-Präferenz inkl. 'system' (für Tristate-Switch)
//   - setTheme(p)   Setzt Präferenz, persistiert Cookie, dispatcht Event
//   - toggle()       Tauscht zwischen 'light' und 'dark' (skipt 'system')
//
// Cross-Tab-/Cross-Komponenten-Synchronisation via CustomEvent
// '37s:theme-change': jeder Hook-Consumer hoert mit, sodass mehrere Switches
// (z. B. Landing-Header + Funnel-Header gleichzeitig auf der Seite) immer
// synchron bleiben.

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export type ThemePref = Theme | 'system';

const COOKIE_KEY = '37s_theme';
const LEGACY_LS_KEY = '37s-admin-theme';
const EVENT_NAME = '37s:theme-change';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 Jahr

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]!) : null;
}

function writeCookie(value: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
}

function systemPref(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolve(pref: ThemePref): Theme {
  if (pref === 'system') return systemPref();
  return pref;
}

function readInitialPref(): ThemePref {
  // SSR: Default light - der echte Wert kommt nach Mount aus dem Cookie.
  // Damit ist der erste Render konsistent mit dem No-FOUC-Skript.
  if (typeof document === 'undefined') return 'light';
  const cookie = readCookie(COOKIE_KEY);
  if (cookie === 'light' || cookie === 'dark' || cookie === 'system') return cookie;
  let legacy: string | null = null;
  try {
    legacy = localStorage.getItem(LEGACY_LS_KEY);
  } catch {
    // ignore
  }
  if (legacy === 'light' || legacy === 'dark') return legacy;
  return 'light';
}

/**
 * useGlobalTheme — Cookie-backed Theme-Hook für alle Public-Pages (Landing,
 * Funnel, Portal, Absage, Success). Identische Quelle wie das No-FOUC-Script
 * im Root-Layout; nach Mount synchronisiert der Hook seinen lokalen State mit
 * dem aktuellen Cookie + Event-Bus.
 */
export function useGlobalTheme() {
  // Erst-Render: 'light' (SSR + initial client) - data-theme ist zu diesem
  // Zeitpunkt schon vom No-FOUC-Skript korrekt gesetzt. Nach Mount übernehmen
  // wir den echten Cookie-Wert und syncen das DOM.
  const [pref, setPref] = useState<ThemePref>('light');
  const [theme, setTheme] = useState<Theme>('light');
  const [mounted, setMounted] = useState(false);

  // Nach Hydration: aktuellen Wert aus Cookie/legacy übernehmen.
  useEffect(() => {
    const initial = readInitialPref();
    const resolved = resolve(initial);
    setPref(initial);
    setTheme(resolved);
    setMounted(true);
    // DOM-Attribut sicherheitshalber neu setzen (sollte schon stimmen).
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolved);
      document.documentElement.setAttribute(
        'data-theme-pref',
        initial === 'system' ? 'system' : resolved,
      );
    }
  }, []);

  // Cross-Komponenten-Sync: anderer Switch hat sich geändert -> hier
  // den State nachziehen.
  useEffect(() => {
    function onChange(evt: Event) {
      const detail = (evt as CustomEvent<{ pref: ThemePref; theme: Theme }>).detail;
      if (!detail) return;
      setPref(detail.pref);
      setTheme(detail.theme);
    }
    window.addEventListener(EVENT_NAME, onChange as EventListener);
    return () => window.removeEventListener(EVENT_NAME, onChange as EventListener);
  }, []);

  // Wenn 'system' aktiv ist und das OS-Theme wechselt, soll der State folgen.
  useEffect(() => {
    if (pref !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function onMq() {
      const next = systemPref();
      setTheme(next);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', next);
      }
    }
    // addEventListener ist seit Safari 14 verfügbar; addListener als Fallback.
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onMq);
      else if (mq.removeListener) mq.removeListener(onMq);
    };
  }, [pref]);

  const apply = useCallback((nextPref: ThemePref) => {
    const resolved = resolve(nextPref);
    setPref(nextPref);
    setTheme(resolved);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolved);
      document.documentElement.setAttribute(
        'data-theme-pref',
        nextPref === 'system' ? 'system' : resolved,
      );
    }
    writeCookie(nextPref);
    // Legacy-localStorage synchron halten (AdminThemeToggle hoert dort).
    try {
      localStorage.setItem(LEGACY_LS_KEY, resolved);
    } catch {
      // ignore (Private-Mode etc.)
    }
    // Cross-Komponenten-Sync: andere Hook-Instanzen + AdminThemeToggle
    // (kann auf das Event hören, wenn er upgraded wird).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(EVENT_NAME, { detail: { pref: nextPref, theme: resolved } }),
      );
    }
  }, []);

  const toggle = useCallback(() => {
    // 'system' im Toggle bewusst überspringgen - der Switch macht binaer
    // light/dark, eine dezidierte System-Wahl gehoert in ein Settings-Menue.
    apply(theme === 'dark' ? 'light' : 'dark');
  }, [apply, theme]);

  return { theme, pref, setTheme: apply, toggle, mounted } as const;
}
